import * as React from 'react';
import { makeStyles } from '@mui/styles';
import { Alert, Box, IconButton, Snackbar, Typography } from '@mui/material';
import { FileRejection, useDropzone } from 'react-dropzone';
import FileUploadingIcon from '../../../icons/FileUploadingIcon.svg';
import { CustomLinearProgressBar } from './CustomLinearProgressBar';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useState } from 'react';

const useStyles = makeStyles((theme) => ({
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: 254,
    borderRadius: '24px',
    background: '#F1F0F5',
    position: 'relative',
    padding: '20px 24px',
    boxSizing: 'border-box',
  },
}));

interface PropsType {
  fileLimit: number;
  files: File[];
  handleFilesAdded: (files: File[]) => void;
  handleFileDeleted: (file: File) => void;
  fileTypes?: string[];
  height?: string;
  description: string;
  maxFileSize: number;
  //TODO: for now these props are optional so that we dont break any existing code, ideally we wanna pass them everywhere this component is used
  validationError?: boolean; //pass this prop as true if there are some sort of validation errors
  syncProgress?: boolean; // pass this prop as true if you want the linear progress bar in sync with an api call
  startProgress?: boolean; // pass this prop when you want to start the linear progress
  stopProgress?: boolean; // pass this prop when you want to stop the progress bar successfully
}

export const AcceptableFileTypes = ['text/plain', 'image/png', 'image/jpeg', 'application/pdf', 'video/mp4', 'video/quicktime']; //temporally

export const CustomDropZone = (props: PropsType) => {
  const classes = useStyles();
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState('');
  const [snackBarOpen, setSnackBarOpen] = useState(false);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  const handleFiles = (files: File[]) => {
    const unSupportedFilesFound = !!files.find((f) => !(props.fileTypes ?? AcceptableFileTypes).includes(f.type));
    if (unSupportedFilesFound) {
      setFileUploadErrorMessage("It seems that one or more of the file type you're trying to upload isn't supported. Please select a file with a different format.");
      setSnackBarOpen(true);
    }
    props.handleFilesAdded(files.filter((f) => (props.fileTypes ?? AcceptableFileTypes).includes(f.type)));
  };

  const handleRejectedFiles = (rejectedFiles: FileRejection[]) => {
    const oversizedFiles = rejectedFiles.filter((rejectedFile: FileRejection) => rejectedFile.file.size > props.maxFileSize * 1024 * 1024);
    if (rejectedFiles.length > props.fileLimit) {
      setFileUploadErrorMessage(`Maximum number of files that can be uploaded at a time is ${props.fileLimit}. Please select up to ${props.fileLimit} files to upload.`);
      setSnackBarOpen(true);
    } else if (oversizedFiles.length > 0) {
      setFileUploadErrorMessage(`One or more files is greater than ${props.maxFileSize}MB. You can upload a zip file. Please try compressing your document then uploading.`);
      setSnackBarOpen(true);
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: handleFiles,
    // It doesn't work for linux OS, so we comment it out for now and do validations manually...
    // accept: {
    //   'image/*': ['.png', '.jpeg', '.jpg'],
    //   'application/pdf': ['.pdf'],
    //   'video/mp4': ['.mp4'],
    // },
    onDropRejected: handleRejectedFiles, // Catch rejected files
    maxFiles: props.fileLimit,
    maxSize: props.maxFileSize * 1024 * 1024,
    noClick: true,
  });

  return (
    <div className="container">
      <div {...getRootProps()} className={classes.dropzone} style={{ maxHeight: props.height ?? 'undefined', border: props.validationError ? '2px solid red' : '' }}>
        <input {...getInputProps()} />
        {props.files.length > 0 ? (
          <Box
            sx={{
              width: '100%',
              maxHeight: 'calc( 100% - 25px )',
              overflow: 'auto',
              marginRight: '-6px',
              paddingRight: '6px',
              '&::-webkit-scrollbar': {
                width: 7,
              },
              '&::-webkit-scrollbar-track': {
                boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                borderRadius: '24px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'darkgrey',
                borderRadius: '24px',
              },
            }}
          >
            {props.files.map((file: any, index: number) => (
              <Box key={index} sx={{ width: '100%', marginBottom: '17px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <Typography
                    style={{
                      fontSize: '18px',
                      lineHeight: '22px',
                      fontFamily: 'Work Sans',
                      color: '#000',
                      maxWidth: 'calc( 100% - 25px )',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {file.name || file}
                  </Typography>

                  {file.name ? (
                    <IconButton aria-label="delete" onClick={() => props.handleFileDeleted(file)} sx={{ padding: '0px' }}>
                      <CloseRoundedIcon sx={{ color: '#c4c4c4', width: 16, height: 16, cursor: 'pointer' }} />
                    </IconButton>
                  ) : (
                    <></>
                  )}
                </Box>

                <CustomLinearProgressBar startProgress={props.startProgress} stopProgress={props.stopProgress} syncProgress={props.syncProgress} />
              </Box>
            ))}
          </Box>
        ) : (
          <>
            <Box
              style={{
                fontSize: '18px',
                lineHeight: '22px',
                fontFamily: 'Work Sans',
                color: '#000',
                marginTop: '24px',
                textAlign: 'center',
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: props.description }} />
            </Box>
            <img src={FileUploadingIcon} alt="" style={{ marginTop: '22px' }} />
          </>
        )}
        {props.files.length < props.fileLimit && (
          <Typography
            style={{
              fontSize: '18px',
              lineHeight: '22px',
              fontFamily: 'Work Sans',
              color: '#000',
              position: 'absolute',
              bottom: '24px',
            }}
          >
            drop a file or{' '}
            <span onClick={open} style={{ color: '#1045FF', cursor: 'pointer' }}>
              browse
            </span>
          </Typography>
        )}
      </div>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {fileUploadErrorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};
